import { Box, Heading, Icons, ThemeContext } from '@vouched.id/vault';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { JobType } from '../jobs/job_type';
import { Card } from './Card';
import { CameraIcon } from './Icons/Camera';
import { JobImages } from './JobImages';
import { JobStatusBar } from './JobStatusBar';
import { LivenessImages } from './LivenessImages';
import { JobResultDropdowns } from './dropdowns/JobResultDropdowns';
import { extractDigitalIDData } from './mdl/DigitalID';
import { calculateJobStatus, jobHasWarnings } from './utils';
import { useGlobalState } from 'store/reducers/reducer';
import { getAmlV2Job, loadJob } from 'store/sagas/sagas';
import { createImages } from 'utils/jobs';
import './mdl/styles.css';
export const JobPage = () => {
    const { asyncDispatch } = useGlobalState();
    const { id: jobSid } = useParams();
    const [job, setJob] = useState(null);
    const [userImages, setUserImages] = useState([]);
    const [frontIdImages, setFrontIdImages] = useState([]);
    const [backIdImages, setBackIdImages] = useState([]);
    const [jobCanHaveImages, setJobCanHaveImages] = useState(false);
    const [jobReviewSuccess, setJobReviewSuccess] = useState(null);
    /** Keyed by job id so a previous job's result is never shown. */
    const [amlV2Load, setAmlV2Load] = useState();
    const { tokens } = useContext(ThemeContext).theme;
    const isAdminRoute = useLocation().pathname.includes('/admin');
    const [showImages, setShowImages] = useState(isAdminRoute);
    useEffect(() => {
        asyncDispatch(loadJob(jobSid, isAdminRoute)).then(setJob, console.error);
    }, [jobSid, isAdminRoute]);
    const jobId = job?.id;
    const jobType = job?.request.type;
    const isRemoved = job?.status === 'removed';
    const shouldLoadAmlV2 = jobType === JobType.idAml && !isRemoved;
    useEffect(() => {
        if (!jobId || !shouldLoadAmlV2)
            return;
        let isCurrent = true;
        asyncDispatch(getAmlV2Job(jobId)).then((amlV2Job) => {
            if (isCurrent)
                setAmlV2Load({ jobId, result: amlV2Job });
        }, (error) => {
            console.error(error);
            if (isCurrent)
                setAmlV2Load({ jobId, result: 'failed' });
        });
        return () => {
            isCurrent = false;
        };
    }, [jobId, shouldLoadAmlV2]);
    useEffect(() => {
        if (!job || job.status === 'removed')
            return;
        if (
        // eslint-disable-next-line unicorn/prefer-includes-over-repeated-comparisons
        job.request.type === JobType.idVerification ||
            job.request.type === JobType.idReverification ||
            job.request.type === JobType.selfieVerification) {
            setJobCanHaveImages(true);
            const { parameters } = job.request;
            if (parameters) {
                setUserImages(createImages(parameters, [
                    'userDetect',
                    'user',
                    'userDistance',
                    'source'
                ]));
                setFrontIdImages(createImages(parameters, ['idDetect', 'id', 'idDistance', 'dest']));
                setBackIdImages(createImages(parameters, ['backId']));
                setJobReviewSuccess(job.reviewSuccess ?? null);
            }
        }
    }, [job]);
    const handleShowImages = useCallback(() => {
        setShowImages((v) => !v);
    }, []);
    // Merge the job returned by the review PUT into job state so the Review
    // panel's persisted-review baseline (job.review) tracks the server without
    // re-fetching the whole job.
    const handleReviewSubmitted = useCallback((updated) => {
        setJobReviewSuccess(updated.reviewSuccess ?? null);
        setJob((prev) => prev
            ? {
                ...prev,
                review: updated.review,
                reviewSuccess: updated.reviewSuccess
            }
            : prev);
    }, []);
    if (!job) {
        return React.createElement(Icons.Loading, null);
    }
    const jobStatus = calculateJobStatus(job.status, job.result?.success);
    // Hold the status until the AML v2 result is in, so it doesn't flash Rejected before Warning
    const amlV2 = shouldLoadAmlV2
        ? amlV2Load?.jobId === job.id
            ? amlV2Load.result
            : 'pending'
        : undefined;
    const isAmlV2WithWarning = typeof amlV2 === 'object' &&
        amlV2.version !== 'v1' &&
        amlV2.initialScreening.approved === false;
    return (React.createElement(Box, { "data-test": "job-v2-view", display: ['block'], paddingX: "spacing.xlarge", paddingY: "spacing.large", style: { boxSizing: 'border-box' }, tag: "main", width: "100%" },
        React.createElement(Box, { marginBottom: "spacing.large" },
            React.createElement(Heading, { style: {
                    fontWeight: tokens.text.weight.bold
                }, tag: "h1", variation: "h3" }, "Jobs")),
        React.createElement(Box, { display: "flex", flexWrap: "wrap", marginBottom: "spacing.large", style: { gap: '15px' } },
            React.createElement(Box, { backgroundColor: "colors.white", flex: ['1 0 70%', '0 0 70%'], paddingX: "spacing.small", paddingY: "spacing.large", style: {
                    borderRadius: '10px',
                    boxShadow: tokens.shadows.hover,
                    boxSizing: 'border-box'
                } }, amlV2 === 'pending' ? (React.createElement(Icons.Loading, null)) : (React.createElement(JobStatusBar, { accountSid: isAdminRoute ? job.accountId : undefined, hasWarnings: jobHasWarnings(job.errors), jobAccountReviewed: !!job.accountReviewed, jobReviewSuccess: jobReviewSuccess, status: isAmlV2WithWarning ? 'warning' : jobStatus, submittedAt: job.submitted, updatedAt: job.updatedAt }))),
            jobCanHaveImages && (React.createElement(Card, { onClick: handleShowImages, title: "Images" },
                React.createElement(CameraIcon, null)))),
        React.createElement(Box, { style: {
                alignItems: 'center'
            } },
            showImages && jobCanHaveImages && (React.createElement(JobImages, { backIdImages: backIdImages, digitalIDData: job.result?.clientOutput?.capture?.id === 'digitalID'
                    ? extractDigitalIDData(job.request, job.result)
                    : undefined, frontIdImages: frontIdImages, hasPDF417Back: job.result?.hasPDF417Back, includeBarcode: !!job.result?.clientOutput?.includeBarcode, isReverification: job.request.type === JobType.idReverification, userImages: userImages })),
            showImages && React.createElement(LivenessImages, { urls: job.livenessAuditUrls ?? [] })),
        jobStatus !== 'removed' && (React.createElement(JobResultDropdowns, { amlV2: amlV2, crossCheckResult: job.result?.crosscheck, job: job, jobDebugResults: job.debugResults, jobErrors: job.errors, jobRequestInfo: job.request.requestInfo, jobRequestParams: job.request.parameters, jobRequestProperties: job.request.properties, jobResult: job.result, jobReview: job.review ?? null, jobSid: job.id, jobStatus: jobStatus, jobType: job.request.type, onReviewSubmitted: handleReviewSubmitted, rejectionReasons: job.rejectionReasons }))));
};
